import { useEffect, useState } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentSources, setPaymentSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category_id: '',
    description: '',
    amount: '',
    paid_by_id: '',
    payment_mode_id: '',
    notes: '',
  });

  useEffect(() => {
    loadExpensesData();
  }, []);

  const loadExpensesData = async () => {
    try {
      const [expensesRes, categoriesRes, modesRes, sourcesRes] = await Promise.all([
        api.get('/expenses'),
        api.get('/categories?type=general'),
        api.get('/payment-modes'),
        api.get('/payment-sources'),
      ]);
      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data);
      setPaymentModes(modesRes.data);
      setPaymentSources(sourcesRes.data);
    } catch (error) {
      console.error('Failed to load expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses', {
        ...formData,
        date: new Date(formData.date).toISOString(),
        amount: parseFloat(formData.amount),
      });
      toast.success('Expense added successfully');
      setDialogOpen(false);
      resetForm();
      loadExpensesData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category_id: '',
      description: '',
      amount: '',
      paid_by_id: '',
      payment_mode_id: '',
      notes: '',
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await api.delete(`/expenses/${id}`);
        toast.success('Expense deleted');
        loadExpensesData();
      } catch (error) {
        toast.error('Failed to delete expense');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const filtered = expenses.filter((e) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      e.description?.toLowerCase().includes(q) ||
      categories.find((c) => c.id === e.category_id)?.name.toLowerCase().includes(q) ||
      paymentSources.find((p) => p.id === e.paid_by_id)?.name.toLowerCase().includes(q);
    const matchFrom = !dateFrom || e.date >= dateFrom;
    const matchTo   = !dateTo   || e.date <= dateTo;
    return matchSearch && matchFrom && matchTo;
  });

  const totalExpenses = filtered.reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading expenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header title="Company Expenses" subtitle="Track and manage your company expenses" />

      <div className="p-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search description, category, paid by…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="bg-white border-slate-200 w-40"
            />
            <span className="text-slate-400 text-sm">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="bg-white border-slate-200 w-40"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="text-xs text-slate-400 hover:text-slate-600 underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
            <p className="text-sm font-medium text-slate-500 mb-1">Total Expenses{(search || dateFrom || dateTo) ? ' (filtered)' : ''}</p>
            <p className="text-3xl font-heading font-bold text-red-600">{formatCurrency(totalExpenses)}</p>
            <p className="text-xs text-slate-400 mt-1">{filtered.length} of {expenses.length} records</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white active:scale-95" data-testid="add-expense-button">
                <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Expense</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddExpense} className="space-y-4" data-testid="add-expense-form">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })} required>
                    <SelectTrigger className="bg-slate-50">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    placeholder="What was this expense for?"
                    className="bg-slate-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Amount (INR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    className="bg-slate-50"
                    data-testid="expense-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Paid By</Label>
                  <Select value={formData.paid_by_id} onValueChange={(value) => setFormData({ ...formData, paid_by_id: value })} required>
                    <SelectTrigger className="bg-slate-50">
                      <SelectValue placeholder="Select who paid" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentSources.map((source) => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Mode</Label>
                  <Select value={formData.payment_mode_id} onValueChange={(value) => setFormData({ ...formData, payment_mode_id: value })} required>
                    <SelectTrigger className="bg-slate-50">
                      <SelectValue placeholder="Select payment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentModes.map((mode) => (
                        <SelectItem key={mode.id} value={mode.id}>
                          {mode.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes"
                    className="bg-slate-50"
                  />
                </div>
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="submit-expense-button">
                  Add Expense
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="expenses-table">
              <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 text-left">Date</th>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Description</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                  <th className="px-6 py-4 text-left">Paid By</th>
                  <th className="px-6 py-4 text-left">Mode</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                      No expenses found
                    </td>
                  </tr>
                ) : (
                  filtered.map((expense) => (
                    <tr key={expense.id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {format(new Date(expense.date), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {categories.find((c) => c.id === expense.category_id)?.name || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">{expense.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-red-600 font-semibold">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {paymentSources.find((p) => p.id === expense.paid_by_id)?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {paymentModes.find((m) => m.id === expense.payment_mode_id)?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Expenses;